/**
 * Browser-offline sell resilience (docs/pospak-business-fit-audit.md §4 —
 * "Browser-offline sell queue"). Scoped down from Dukaan-POS's full
 * cold-start-offline architecture (a local catalog cache + fully offline
 * bill creation is a much larger undertaking, tracked separately):
 *
 * WHAT THIS COVERS: adding a line to a bill that ALREADY has a server-side
 * id (the cashier has already scanned/searched at least one item while
 * online). If the network drops mid-sale — the single most common real-
 * world "offline" case for a small shop, a few seconds to a few minutes,
 * not hours — every subsequent scan/search still resolves from whatever
 * the browser already has cached, and each "add line" call that fails with
 * a genuine network error (not a server rejection) is queued here instead
 * of shown as an error. The line appears immediately with a "pending sync"
 * badge; `useOfflineSync` replays the queue the moment connectivity
 * returns, in the order items were added, using `clientOpId` so a replay
 * can never double-add.
 *
 * WHAT THIS DELIBERATELY DOES NOT COVER: starting a brand new sale (the
 * very first line, before the bill has a server id) while offline — that
 * still requires one round trip to create the bill shell. Making that work
 * too needs a local product/price cache kept in sync while online, which is
 * its own project, not bolted on here.
 */
import { api, ApiClientError } from './api';
import { enqueueOp, listQueuedOps, removeOp, markOpFailed, type QueuedLineOp } from './offline-db';
import type { Bill } from '../features/selling';

/** A `fetch()` that never reached the network throws a plain `TypeError`
 * (or, in some browsers, `DOMException` on abort) — never an
 * `ApiClientError`, which only exists once a response actually came back.
 * That distinction is exactly "queue this" vs "the server said no". */
export function isNetworkFailure(err: unknown): boolean {
  return !(err instanceof ApiClientError);
}

export async function queueAddLine(op: QueuedLineOp): Promise<void> {
  await enqueueOp(op);
}

/** Items still waiting to sync — excludes FAILED ones, which need manual attention (features/offline-sync) rather than "will sync when connection returns". */
export async function pendingCount(): Promise<number> {
  return (await listQueuedOps()).filter((o) => o.status === 'PENDING').length;
}

/**
 * Replays every queued op for one bill, oldest first, stopping at the
 * first one that still fails (so ordering is never scrambled by a partial
 * replay). Returns the final server bill state if anything synced, so the
 * caller can refresh its view.
 */
export async function replayQueueForBill(billId: string): Promise<Bill | null> {
  const ops = (await listQueuedOps()).filter((o) => o.billId === billId && o.status === 'PENDING');
  let latest: Bill | null = null;
  for (const op of ops) {
    try {
      latest = await api.post<Bill>(`/bills/${billId}/lines`, op.body);
      await removeOp(op.clientOpId);
    } catch (err) {
      if (isNetworkFailure(err)) {
        // Still offline — stop here, the rest will retry next time.
        break;
      }
      // The server rejected it for real (stock, permission, bill no longer
      // editable, …) — leave it visible as FAILED rather than silently
      // dropping a sale someone thought was queued.
      await markOpFailed(op.clientOpId, err instanceof Error ? err.message : 'Sync failed');
    }
  }
  return latest;
}

/** Every bill id with at least one queued op — `useOfflineSync` replays each in turn. */
export async function queuedBillIds(): Promise<string[]> {
  const ops = await listQueuedOps();
  return [...new Set(ops.filter((o) => o.status === 'PENDING').map((o) => o.billId))];
}
